/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.sprudel.pattern

import io.peekandpoke.klang.common.math.CycleTime

import io.peekandpoke.klang.sprudel.SprudelPattern
import io.peekandpoke.klang.sprudel.SprudelPattern.QueryContext
import io.peekandpoke.klang.sprudel.SprudelPatternEvent
import io.peekandpoke.klang.sprudel.sampleAt

/**
 * Handles structure combination logic (In/Out) with optional filtering.
 *
 * - [mode] determines which pattern provides the structure (In = Source, Out = Other).
 * - [filterByTruthiness] determines if we check the value of the 'other' pattern (true = keepif, false = keep).
 */
internal class StructurePattern(
    val source: SprudelPattern,
    val other: SprudelPattern,
    val mode: Mode,
    val filterByTruthiness: Boolean,
) : SprudelPattern {

    enum class Mode {
        In,  // Source provides structure (Mask behavior)
        Out  // Other provides structure (Struct behavior)
    }

    override val weight: Double = if (mode == Mode.In) source.weight else other.weight

    override val numSteps: Double?
        get() = when (mode) {
            Mode.In -> source.numSteps
            Mode.Out -> other.numSteps
        }

    override fun estimateCycleDuration(): Double {
        return when (mode) {
            Mode.In -> source.estimateCycleDuration()
            Mode.Out -> other.estimateCycleDuration()
        }
    }

    override fun queryArcContextual(from: CycleTime, to: CycleTime, ctx: QueryContext): List<SprudelPatternEvent> {
        return when (mode) {
            Mode.In -> queryIn(from, to, ctx)
            Mode.Out -> queryOut(from, to, ctx)
        }
    }

    /**
     * In Mode (mask/maskAll): The source provides the structure.
     * We iterate over source events and sample the 'other' pattern at the midpoint to decide inclusion.
     */
    private fun queryIn(from: CycleTime, to: CycleTime, ctx: QueryContext): List<SprudelPatternEvent> {
        val sourceEvents = source.queryArcContextual(from, to, ctx)
        val result = createEventList()

        for (sourceEvent in sourceEvents) {
            val mid = (sourceEvent.part.begin + sourceEvent.part.end).divBy(2.0)

            // Sample the other pattern at the midpoint
            val otherEvent = other.sampleAt(mid, ctx)

            val keep = if (filterByTruthiness) {
                // keepif: keep only if overlapping event is truthy
                otherEvent?.data?.isTruthy() == true
            } else {
                // keep: keep if any overlapping event exists
                otherEvent != null
            }

            if (keep) {
                // The mask atom that let the event through lights up too, the way struct's does
                result.add(sourceEvent.prependLocations(otherEvent?.sourceLocations))
            }
        }
        return result
    }

    /**
     * Out Mode (struct/structAll): The 'other' pattern provides the structure.
     * We behave like an intersection: clipping source events to the mask's duration.
     */
    private fun queryOut(from: CycleTime, to: CycleTime, ctx: QueryContext): List<SprudelPatternEvent> {
        // Optimization: Inline bind logic to avoid lambda allocation and ReinterpretPattern overhead
        val maskEvents = other.queryArcContextual(from, to, ctx)
        val result = createEventList()

        for (maskEvent in maskEvents) {
            // If filtering by truthiness, skip falsy mask events
            if (filterByTruthiness && !maskEvent.data.isTruthy()) {
                continue
            }

            // Intersect query arc with mask event
            val intersectStart = maxOf(from, maskEvent.part.begin)
            val intersectEnd = minOf(to, maskEvent.part.end)

            if (intersectEnd <= intersectStart) continue

            // Query source pattern within the mask event's duration
            val sourceEvents = source.queryArcContextual(intersectStart, intersectEnd, ctx)

            for (sourceEvent in sourceEvents) {
                // Clip source event to mask event boundaries
                val clippedPart = sourceEvent.part.clipTo(maskEvent.part)

                if (clippedPart != null) {
                    // Create the final event with mask's whole
                    // struct "rebirths" events within mask boundaries:
                    // so whole is set to mask boundaries, not preserved from source
                    val finalEvent = sourceEvent.copy(
                        part = clippedPart,
                        whole = maskEvent.whole  // the structuring pattern provides the whole
                    ).prependLocations(maskEvent.sourceLocations)

                    result.add(finalEvent)
                }
            }
        }

        return result
    }
}
